import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { canWithdraw } from '../logic/offboarding';
import { WITHDRAWAL_REQUEST_ON, lockInEnd, formatLockInDate } from '../logic/withdrawalRequest';

// "At a glance" patient reference: administrative / contact info a care-team member
// checks without leaving the file — NO clinical data (readings, conditions, meds,
// care plan). Presentational apart from one action: withdrawal lives here because
// offboarding is administrative, not part of the day-to-day work surface.

const MONO = 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace';

// fields = [{ label, value, mono }]. A field with an empty value is dropped; a
// section whose fields all dropped renders nothing.
function Section({ title, fields }) {
  const visible = fields.filter(f => f.value != null && f.value !== '');
  if (visible.length === 0) return null;
  return (
    <section>
      <div className="eyebrow text-secondary" style={{ margin: '0 0 10px' }}>{title}</div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: '14px 20px' }}>
        {visible.map(f => (
          <div key={f.label} style={{ minWidth: 0 }}>
            <span className="detail1 text-secondary">{f.label}</span>
            <p className="body2" style={{ margin: '2px 0 0', wordBreak: 'break-word', fontFamily: f.mono ? MONO : undefined }}>
              {f.value}
            </p>
          </div>
        ))}
      </div>
    </section>
  );
}

function formatAddress(record) {
  if (!record) return null;
  const street = [record.address_line1, record.address_line2].filter(Boolean).join(', ');
  const cityLine = [record.city, [record.state, record.zip_code].filter(Boolean).join(' ')]
    .filter(Boolean).join(', ');
  const lines = [street, cityLine].filter(Boolean);
  if (lines.length === 0) return null;
  return lines.map((l, i) => <span key={i} style={{ display: 'block' }}>{l}</span>);
}

export default function PatientInfoModal({
  patient, patientRecord, insurance, phone, email, birthdate, loading, onClose,
  carePeriod = null, onWithdraw = null, onUnalign = null, unalignIsTestReset = false,
  requestOn = WITHDRAWAL_REQUEST_ON,
}) {
  // One clock read per open: the lock-in date must not move under the user.
  const [nowMs] = useState(() => Date.now());
  useEffect(() => {
    // Capture + stopPropagation, and RESTORE the previous overflow: the patient drawer
    // holds its own window Escape listener (which navigates it closed) and its own body
    // scroll lock, so a bubbling Escape would also throw the user back to the board and
    // clearing overflow would unlock the still-open drawer.
    function onKey(e) {
      if (e.key !== 'Escape') return;
      e.stopPropagation();
      onClose();
    }
    window.addEventListener('keydown', onKey, true);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey, true);
      document.body.style.overflow = prevOverflow;
    };
  }, [onClose]);

  const rec = patientRecord || {};
  const firstName = rec.first_name || patient?.firstname || null;
  const lastName = rec.last_name || patient?.lastname || null;
  const dob = birthdate ? `${birthdate.display}${birthdate.age != null ? ` (${birthdate.age})` : ''}` : null;
  const smsOptIn = typeof rec.sms_opt_in === 'boolean' ? (rec.sms_opt_in ? 'Enabled' : 'Disabled') : null;
  const ehrId = rec.ehr_patient_id ?? patient?.ehr_patient_id ?? null;

  const identity = [
    { label: 'First name', value: firstName },
    { label: 'Last name', value: lastName },
    { label: 'Date of birth', value: dob },
    { label: 'Sex', value: rec.sex || null },
    { label: 'Email', value: email },
    { label: 'Phone', value: phone, mono: true },
    { label: 'SMS notifications', value: smsOptIn },
    { label: 'Mailing address', value: formatAddress(rec) },
  ];
  const medicare = [{ label: 'MBI', value: insurance?.member_id || null, mono: true }];
  const pcp = [
    { label: 'Name', value: rec.pcp_name },
    { label: 'Specialty', value: rec.pcp_specialty },
    { label: 'Phone', value: rec.pcp_phone, mono: true },
    { label: 'NPI', value: rec.pcp_npi, mono: true },
  ];
  const enrollment = [
    { label: 'Withings user ID', value: patient?.userid != null ? String(patient.userid) : null, mono: true },
    { label: 'Elation patient ID', value: ehrId != null ? String(ehrId) : null, mono: true },
    { label: 'Program', value: rec.program || patient?.program || null },
    { label: 'Timezone', value: rec.timezone },
  ];
  const withdrawGate = onWithdraw ? canWithdraw(carePeriod, { requestOn }) : { visible: false };
  const lockIn = withdrawGate.mode === 'request' ? lockInEnd(carePeriod, nowMs) : null;

  return createPortal((
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={e => e.stopPropagation()} style={{ maxWidth: 620, width: '90vw' }}>
        <div className="modal-header">
          <div>
            <h2 className="header3" style={{ margin: 0 }}>Patient information</h2>
            <p className="detail1 text-secondary" style={{ margin: '4px 0 0', textTransform: 'capitalize' }}>
              {[firstName, lastName].filter(Boolean).join(' ') || `Patient ${patient?.userid}`}
            </p>
          </div>
          <button className="modal-close" onClick={onClose} aria-label="Close">&times;</button>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 22, paddingTop: 4 }}>
          {loading && !patientRecord ? (
            <div className="body2 text-secondary">Loading patient details…</div>
          ) : (
            <>
              <Section title="Identity & contact" fields={identity} />
              <Section title="Medicare" fields={medicare} />
              <Section title="Primary care provider" fields={pcp} />
              <Section title="Identifiers & enrollment" fields={enrollment} />
              {(withdrawGate.visible || onUnalign) && (
                <section style={{ paddingTop: 18, borderTop: '1px solid var(--wi-divider)' }}>
                  <div className="eyebrow text-secondary" style={{ margin: '0 0 10px' }}>Offboarding</div>
                  {withdrawGate.visible && (
                    <>
                      {withdrawGate.mode === 'request' ? (
                        <p className="detail1 text-secondary" style={{ margin: '0 0 12px', maxWidth: 460 }}>
                          {`Aligned with CMS, so this saves a withdrawal request. ${lockIn ? `Unalignment possible from ${formatLockInDate(lockIn.ymd)}.` : 'Alignment date unknown.'}`}
                        </p>
                      ) : (
                        <p className="detail1 text-secondary" style={{ margin: '0 0 12px', maxWidth: 460 }}>
                          Withdrawing stops every SMS, email and chat, unsubscribes their Withings
                          devices, and takes them off the care-team board. The record is kept &mdash;
                          they can be re-enrolled later.
                        </p>
                      )}
                      {/* Clickable even when blocked — the withdrawal modal is where the block
                          is explained, with the unalign route. A disabled button was a dead end. */}
                      <button
                        className="btn-secondary btn-sm"
                        onClick={onWithdraw}
                        aria-describedby={!withdrawGate.enabled && withdrawGate.reason ? 'withdraw-blocker' : undefined}
                      >
                        Withdraw patient
                      </button>
                      {!withdrawGate.enabled && withdrawGate.reason && (
                        <p id="withdraw-blocker" className="detail1 text-secondary" style={{ margin: '8px 0 0' }}>{withdrawGate.reason}</p>
                      )}
                    </>
                  )}
                  {onUnalign && (
                    <>
                      <p className="detail1 text-secondary" style={{ margin: withdrawGate.visible ? '18px 0 12px' : '0 0 12px', maxWidth: 460 }}>
                        {unalignIsTestReset
                          ? 'Test only — not shown on production. Submits CMS unalignment whatever the care-period status.'
                          : 'Unaligning tells CMS this beneficiary leaves the model. It ends the care period, and the reporting clock with it.'}
                      </p>
                      <button
                        className="btn-secondary btn-sm"
                        onClick={onUnalign}
                      >
                        {unalignIsTestReset ? 'Unalign (test reset)' : 'Unalign from CMS'}
                      </button>
                    </>
                  )}
                </section>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  ), document.body);
}
